/**
 * RPD.DESIGNER — Report Designer (licensed; menu-gated via the `rpd` module).
 *
 * A functional banded-report designer (spec §10): pick a BANDED_REPORT template,
 * see its bands as top-to-bottom labelled strips with elements drawn as
 * absolutely-positioned boxes at (x_pt,y_pt,width_pt,height_pt); edit an element
 * in a Property Inspector modal (position/size, type, field/expression, format
 * mask, style); add/remove bands and elements; and render a Live Preview through
 * the doc-worker preview endpoint (one engine, one truth).
 *
 * Every write funnels through the Common Routine (routed_writes). Definition
 * writes may be rejected with RPD.NOT_LICENSED at the gateway — that is the
 * licence gate working; the error surfaces to the user. In dev/self-host it
 * succeeds. All preview text is escaped and drawn inside a sandboxed <iframe>
 * WITHOUT allow-scripts, so no data-derived string is ever injected as markup.
 *
 * Platform new-page contract (§17): self-wraps PersonalizationProvider, exposes a
 * PersonalizationToggle, renders the bands/elements LISTS via PersonalizedGrid.
 * The canvas + preview are purpose-built surfaces (not list data), so they are
 * not PersonalizedGrids.
 */
/* tl-group-ok: the expand/contract control here is on report BANDS and ELEMENTS, and
   doc_report_band has no _tl. The embedded doc_format_mask_tl localises the format-mask
   dropdown (§17.B4). Templates are a <select>, not an expandable grid — and doc_template_tl
   is a CONTENT table (title/body_html), not a display-name one, so it carries nothing this
   panel could list. */
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { apiDb } from '../../lib/api_client';
import { supabase } from '../../lib/supabase_client';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import { useActiveLocale } from '../../lib/use_active_locale';
import type { AuthSession } from '../../types/auth';
import { Modal } from '../../components/common/Modal';
import { EmptyState } from '../../components/common/EmptyState';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'RPD.DESIGNER';

const DESIGNER_PAGE = 'RPD.DESIGNER';
const WORKER_URL = `${import.meta.env.VITE_SUPABASE_URL as string}/functions/v1/doc-worker`;
const PT_PER_PX = 1; // canvas draws 1px per point (scaled by CANVAS_SCALE for fit)
const CANVAS_SCALE = 0.9;

// ── CHECK-enum labelled constants (§17.B11) — stored value is the code; the label
// is localized and is the ONLY thing rendered in cells/facets/options/labels. ──
interface EnumOpt { value: string; labelKey: string; fallback: string; }

const BAND_TYPES: EnumOpt[] = [
  { value: 'REPORT_HEADER', labelKey: 'rpd.band_type.REPORT_HEADER', fallback: 'Report Header' },
  { value: 'PAGE_HEADER',   labelKey: 'rpd.band_type.PAGE_HEADER',   fallback: 'Page Header' },
  { value: 'GROUP_HEADER',  labelKey: 'rpd.band_type.GROUP_HEADER',  fallback: 'Group Header' },
  { value: 'DETAIL',        labelKey: 'rpd.band_type.DETAIL',        fallback: 'Detail' },
  { value: 'GROUP_FOOTER',  labelKey: 'rpd.band_type.GROUP_FOOTER',  fallback: 'Group Footer' },
  { value: 'PAGE_FOOTER',   labelKey: 'rpd.band_type.PAGE_FOOTER',   fallback: 'Page Footer' },
  { value: 'REPORT_FOOTER', labelKey: 'rpd.band_type.REPORT_FOOTER', fallback: 'Report Footer' },
];

// Band types that stand alone (no group reference) — the add-band picker offers these.
const NON_GROUP_BAND_TYPES = BAND_TYPES.filter(b => b.value !== 'GROUP_HEADER' && b.value !== 'GROUP_FOOTER');

const ELEMENT_TYPES: EnumOpt[] = [
  { value: 'LABEL',      labelKey: 'rpd.element_type.LABEL',      fallback: 'Label' },
  { value: 'FIELD',      labelKey: 'rpd.element_type.FIELD',      fallback: 'Field' },
  { value: 'EXPRESSION', labelKey: 'rpd.element_type.EXPRESSION', fallback: 'Expression' },
  { value: 'AGGREGATE',  labelKey: 'rpd.element_type.AGGREGATE',  fallback: 'Aggregate' },
  { value: 'SYSFIELD',   labelKey: 'rpd.element_type.SYSFIELD',   fallback: 'System Field' },
  { value: 'IMAGE',      labelKey: 'rpd.element_type.IMAGE',      fallback: 'Image' },
  { value: 'LINE',       labelKey: 'rpd.element_type.LINE',       fallback: 'Line' },
  { value: 'RECT',       labelKey: 'rpd.element_type.RECT',       fallback: 'Rectangle' },
  { value: 'BARCODE',    labelKey: 'rpd.element_type.BARCODE',    fallback: 'Barcode' },
];

const ALIGNMENTS: EnumOpt[] = [
  { value: 'left',   labelKey: 'rpd.align.left',   fallback: 'Left' },
  { value: 'center', labelKey: 'rpd.align.center', fallback: 'Center' },
  { value: 'right',  labelKey: 'rpd.align.right',  fallback: 'Right' },
];

interface TemplateRow { doc_template_id: number; internal_code: string; name: string; page_size_code: string | null }
interface GroupRow { doc_report_group_id: number; group_level: number; tracking_field: string }
interface BandRow {
  doc_report_band_id: number; doc_template_id: number; band_type: string;
  doc_report_group_id: number | null; height_pt: number; sort_order: number;
}
interface ElementStyle { font_size?: number; bold?: boolean; italic?: boolean; align?: 'left' | 'center' | 'right'; color?: string; fill?: string; border?: boolean; padding?: number }
interface ElementRow {
  doc_report_element_id: number; doc_report_band_id: number; element_seq: number;
  element_type: string; x_pt: number; y_pt: number; width_pt: number; height_pt: number;
  expression: string | null; field_ref: string | null; format_mask_code: string | null;
  style: ElementStyle; z_order: number;
}
interface FormatMaskOption { format_mask_code: string; name: string }
interface PageSizeRow { page_size_code: string; width_pt: number; height_pt: number }

// Preview primitives returned by the doc-worker preview endpoint.
interface PrimBase { x: number; y: number; width: number; height: number }
type Primitive =
  | (PrimBase & { kind: 'text'; text: string; style?: ElementStyle })
  | (PrimBase & { kind: 'line'; color?: string })
  | (PrimBase & { kind: 'rect'; fill?: string; border?: boolean; color?: string })
  | (PrimBase & { kind: 'image'; resource_code?: string });
interface PreviewPage { pageNo: number; primitives: Primitive[] }

/** HTML-escape any data-derived string before it reaches a browser surface. */
function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/* ══════════════════════════════════════════════════════════════════════════ */

function DesignerInner({ session }: { session: AuthSession }) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const locale = useActiveLocale();
  const [templates, setTemplates] = useState<TemplateRow[]>([]);
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [bands, setBands] = useState<BandRow[]>([]);
  const [elements, setElements] = useState<ElementRow[]>([]);
  const [masks, setMasks] = useState<FormatMaskOption[]>([]);
  const [pageSizes, setPageSizes] = useState<PageSizeRow[]>([]);
  const [editingEl, setEditingEl] = useState<ElementRow | null>(null);
  const [addingBand, setAddingBand] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Live preview state.
  const [preview, setPreview] = useState<PreviewPage[] | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);

  const bandLabel = (v: string) => { const o = BAND_TYPES.find(b => b.value === v); return o ? t(o.labelKey, o.fallback) : v; };
  const elementLabel = (v: string) => { const o = ELEMENT_TYPES.find(e => e.value === v); return o ? t(o.labelKey, o.fallback) : v; };
  const maskName = (code: string) => { const m = masks.find(x => x.format_mask_code === code); return m ? `${m.format_mask_code} — ${m.name}` : code; };

  // Load the picker list + reference data once.
  useEffect(() => {
    void (async () => {
      const [tpl, mk, ps] = await Promise.all([
        apiDb.from('doc_template')
          .select('doc_template_id, internal_code, name, page_size_code')
          .eq('template_kind', 'BANDED_REPORT').order('internal_code'),
        apiDb.from('doc_format_mask')
          .select('format_mask_code, doc_format_mask_tl ( locale_code, display_text )')
          .eq('is_active', 'Y').order('sort_order'),
        apiDb.from('doc_page_size').select('page_size_code, width_pt, height_pt'),
      ]);
      setTemplates((tpl.data ?? []) as TemplateRow[]);
      type MaskRaw = { format_mask_code: string; doc_format_mask_tl?: { locale_code: string; display_text: string }[] };
      setMasks(((mk.data ?? []) as MaskRaw[]).map(m => {
        const tl = m.doc_format_mask_tl ?? [];
        const name = tl.find(x => x.locale_code === locale)?.display_text
          ?? tl.find(x => x.locale_code === 'EN_US')?.display_text ?? m.format_mask_code;
        return { format_mask_code: m.format_mask_code, name };
      }));
      setPageSizes((ps.data ?? []) as PageSizeRow[]);
    })();
  }, [locale]);

  const loadDefinition = async (tid: number) => {
    setBusy(true); setError(null);
    try {
      const [g, b, el] = await Promise.all([
        apiDb.from('doc_report_group').select('doc_report_group_id, group_level, tracking_field').eq('doc_template_id', tid).order('group_level'),
        apiDb.from('doc_report_band').select('doc_report_band_id, doc_template_id, band_type, doc_report_group_id, height_pt, sort_order').eq('doc_template_id', tid).order('sort_order'),
        apiDb.from('doc_report_element')
          .select('doc_report_element_id, doc_report_band_id, element_seq, element_type, x_pt, y_pt, width_pt, height_pt, expression, field_ref, format_mask_code, style, z_order, doc_report_band!inner(doc_template_id)')
          .eq('doc_report_band.doc_template_id', tid).order('element_seq'),
      ]);
      if (g.error) throw g.error; if (b.error) throw b.error; if (el.error) throw el.error;
      setGroups((g.data ?? []) as GroupRow[]);
      setBands((b.data ?? []) as BandRow[]);
      setElements(((el.data ?? []) as ElementRow[]).map(e => ({ ...e, style: e.style ?? {} })));
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const selectTemplate = (tid: number | null) => {
    setTemplateId(tid); setPreview(null); setGroups([]); setBands([]); setElements([]);
    if (tid != null) void loadDefinition(tid);
  };

  const selectedTemplate = templates.find(x => x.doc_template_id === templateId) ?? null;
  const pageSize = useMemo(
    () => pageSizes.find(p => p.page_size_code === selectedTemplate?.page_size_code) ?? { page_size_code: '', width_pt: 595, height_pt: 842 },
    [pageSizes, selectedTemplate],
  );

  // Resolve a group band's group tracking-field label (never the raw id).
  const bandDisplay = (band: BandRow) => {
    if (band.doc_report_group_id == null) return bandLabel(band.band_type);
    const grp = groups.find(g => g.doc_report_group_id === band.doc_report_group_id);
    return grp ? `${bandLabel(band.band_type)} · ${grp.tracking_field}` : bandLabel(band.band_type);
  };

  const elementsForBand = (bandId: number) => elements.filter(e => e.doc_report_band_id === bandId);

  // ── Add / remove bands and elements (routed writes → Common Routine) ────────
  const addBand = async (bandType: string, heightPt: number) => {
    if (templateId == null) return;
    setBusy(true); setError(null);
    try {
      const { error: e } = await routedInsert(session, 'doc_report_band', {
        doc_template_id: templateId, band_type: bandType, height_pt: heightPt,
        sort_order: bands.length > 0 ? Math.max(...bands.map(b => b.sort_order)) + 10 : 10,
      });
      if (e) throw new Error(e.message);
      setAddingBand(false);
      await loadDefinition(templateId);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const removeBand = async (band: BandRow) => {
    if (templateId == null) return;
    setBusy(true); setError(null);
    try {
      const { error: e } = await routedDelete(session, 'doc_report_band', 'doc_report_band_id', band.doc_report_band_id);
      if (e) throw new Error(e.message);
      await loadDefinition(templateId);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const addElement = async (band: BandRow) => {
    if (templateId == null) return;
    setBusy(true); setError(null);
    try {
      const seqs = elementsForBand(band.doc_report_band_id).map(e => e.element_seq);
      const { error: e } = await routedInsert(session, 'doc_report_element', {
        doc_report_band_id: band.doc_report_band_id,
        element_seq: seqs.length > 0 ? Math.max(...seqs) + 1 : 1,
        element_type: 'LABEL', x_pt: 4, y_pt: 4, width_pt: 120, height_pt: 16,
        style: {},
      });
      if (e) throw new Error(e.message);
      await loadDefinition(templateId);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const removeElement = async (el: ElementRow) => {
    if (templateId == null) return;
    setBusy(true); setError(null);
    try {
      const { error: e } = await routedDelete(session, 'doc_report_element', 'doc_report_element_id', el.doc_report_element_id);
      if (e) throw new Error(e.message);
      setEditingEl(null);
      await loadDefinition(templateId);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const saveElement = async (patch: Partial<ElementRow>, el: ElementRow): Promise<string | null> => {
    const { error: e } = await routedUpdate(session, 'doc_report_element', 'doc_report_element_id', el.doc_report_element_id, {
      element_type: patch.element_type,
      x_pt: patch.x_pt, y_pt: patch.y_pt, width_pt: patch.width_pt, height_pt: patch.height_pt,
      field_ref: patch.field_ref ?? null, expression: patch.expression ?? null,
      format_mask_code: patch.format_mask_code ?? null, style: patch.style ?? {},
    });
    if (e) return e.message;
    if (templateId != null) await loadDefinition(templateId);
    return null;
  };

  // ── Live preview via the doc-worker preview endpoint ────────────────────────
  const runPreview = async () => {
    if (templateId == null) return;
    setPreviewBusy(true); setError(null);
    try {
      const { data: sd } = await supabase.auth.getSession();
      const token = sd.session?.access_token ?? '';
      const resp = await fetch(WORKER_URL, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ mode: 'preview', template_id: templateId, params: {}, locale, limit: 50 }),
      });
      const json = await resp.json().catch(() => ({ ok: false, error: 'RPD.PREVIEW.BAD_RESPONSE' }));
      if (!json.ok) throw new Error(json.error ?? t('rpd.err.preview_failed', 'Preview could not be rendered.'));
      setPreview((json.pages ?? []) as PreviewPage[]);
    } catch (e) { setError((e as Error).message); setPreview(null); } finally { setPreviewBusy(false); }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="text-csm-sm font-semibold uppercase tracking-wider text-csm-ink-soft">{t('rpd.title', 'Report Designer')}</span>
        <label className="text-csm-xs text-csm-ink-soft">{t('rpd.template', 'Template')}</label>
        <select
          value={templateId ?? ''}
          onChange={e => selectTemplate(e.target.value ? Number(e.target.value) : null)}
          className="h-7 w-72 rounded border border-csm-border px-2 text-csm-sm"
        >
          <option value="">{t('rpd.pick_template', 'Select a banded report…')}</option>
          {templates.map(tpl => (
            <option key={tpl.doc_template_id} value={tpl.doc_template_id}>{tpl.name ? `${tpl.internal_code} — ${tpl.name}` : tpl.internal_code}</option>
          ))}
        </select>
        {templateId != null && (
          <>
            {canAdd && (<button onClick={() => setAddingBand(true)} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">+ {t('rpd.add_band_lbl', 'Band')}</button>)}
            <button onClick={() => void runPreview()} disabled={previewBusy} className="h-7 rounded border border-primary bg-primary px-2 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{previewBusy ? t('rpd.previewing', 'Rendering…') : t('rpd.preview', 'Live Preview')}</button>
          </>
        )}
        {busy && <span className="text-csm-xs text-csm-watermark">{t('common.loading', 'Loading…')}</span>}
        {error && <span className="max-w-md truncate text-csm-xs text-csm-danger" title={error}>{error}</span>}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>

      {templateId == null ? (
        <div className="flex-1 overflow-auto p-6">
          <EmptyState title={t('rpd.empty', 'Select a banded report to design')} description={t('rpd.empty_hint', 'Only BANDED_REPORT templates are edited in the designer.')} />
        </div>
      ) : (
        <div className="flex flex-1 overflow-hidden">
          {/* ── Left: canvas + lists ───────────────────────────────────────── */}
          <div className="flex-1 overflow-auto p-3">
            {/* Visual band canvas */}
            <div className="mb-4">
              <div className="mb-1 text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('rpd.canvas', 'Layout Canvas')}</div>
              <div className="inline-block rounded border border-csm-border bg-csm-surface-alt p-2">
                {bands.length === 0 ? (
                  <div className="px-8 py-6"><EmptyState variant="no-data" title={t('rpd.no_bands', 'No bands yet')} /></div>
                ) : (
                  <div style={{ width: pageSize.width_pt * PT_PER_PX * CANVAS_SCALE }}>
                    {bands.map(band => {
                      const h = Math.max(band.height_pt, 24) * PT_PER_PX * CANVAS_SCALE;
                      return (
                        <div key={band.doc_report_band_id} className="mb-1 border border-csm-border-strong bg-white">
                          <div className="flex items-center gap-2 border-b border-csm-border-soft bg-csm-surface-alt px-2 py-0.5">
                            <span className="text-csm-2xs font-semibold uppercase tracking-wider text-csm-ink-soft">{bandDisplay(band)}</span>
                            <button onClick={() => void addElement(band)} className="ml-auto rounded border border-csm-border bg-white px-1.5 text-csm-2xs hover:bg-csm-surface-alt">+ {t('rpd.element', 'Element')}</button>
                            <button onClick={() => void removeBand(band)} className="text-csm-danger text-csm-2xs">{t('rpd.remove', 'remove')}</button>
                          </div>
                          <div className="relative" style={{ height: h }}>
                            {elementsForBand(band.doc_report_band_id).map(el => (
                              canChange && (<button
                                key={el.doc_report_element_id}
                                onClick={() => setEditingEl(el)}
                                title={elementLabel(el.element_type)}
                                className="absolute overflow-hidden rounded-sm border border-primary/60 bg-primary/10 px-1 text-left text-csm-2xs text-csm-ink hover:bg-primary/20"
                                style={{
                                  left: el.x_pt * PT_PER_PX * CANVAS_SCALE,
                                  top: el.y_pt * PT_PER_PX * CANVAS_SCALE,
                                  width: Math.max(el.width_pt, 8) * PT_PER_PX * CANVAS_SCALE,
                                  height: Math.max(el.height_pt, 8) * PT_PER_PX * CANVAS_SCALE,
                                  fontWeight: el.style?.bold ? 700 : 400,
                                  textAlign: el.style?.align ?? 'left',
                                }}
                              >
                                {elementLabel(el.element_type)}{el.field_ref ? ` · ${el.field_ref}` : ''}
                              </button>)
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Bands list (list data → PersonalizedGrid) */}
            <BandsGrid session={session} bands={bands} bandDisplay={bandDisplay} countFor={id => elementsForBand(id).length}
              onRemove={b => void removeBand(b)} />

            {/* Elements list (list data → PersonalizedGrid) */}
            <ElementsGrid session={session} elements={elements} bandDisplay={bandId => {
              const b = bands.find(x => x.doc_report_band_id === bandId); return b ? bandDisplay(b) : '';
            }} elementLabel={elementLabel} maskName={maskName} onEdit={el => setEditingEl(el)} />
          </div>

          {/* ── Right: preview panel ───────────────────────────────────────── */}
          <div className="w-[420px] shrink-0 overflow-auto border-l border-csm-border bg-white p-3">
            <div className="mb-1 text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('rpd.live_preview', 'Live Preview')}</div>
            {preview == null ? (
              <p className="text-csm-xs text-csm-muted">{t('rpd.preview_hint', 'Click Live Preview to render the current definition.')}</p>
            ) : (
              <PreviewSurface pages={preview} pageSize={pageSize} />
            )}
          </div>
        </div>
      )}

      {editingEl && (
        <PropertyInspector
          element={editingEl}
          masks={masks}
          maskName={maskName}
          onSave={saveElement}
          onRemove={() => void removeElement(editingEl)}
          onClose={() => setEditingEl(null)}
        />
      )}
      {addingBand && (
        <AddBandModal existing={bands.map(b => b.band_type)} onAdd={(bt, h) => void addBand(bt, h)} onClose={() => setAddingBand(false)} />
      )}
    </div>
  );
}

/* ── Bands list grid ────────────────────────────────────────────────────── */
const BAND_COLS: GridColumnDef[] = [
  { key: 'band', header: 'Band', defaultWidth: 240 },
  { key: 'height_pt', header: 'Height (pt)', defaultWidth: 120 },
  { key: 'elements', header: 'Elements', defaultWidth: 100 },
];

function BandsGrid({ session, bands, bandDisplay, countFor, onRemove }: {
  session: AuthSession;
  bands: BandRow[]; bandDisplay: (b: BandRow) => string; countFor: (bandId: number) => number; onRemove: (b: BandRow) => void;
}) {
  const { t } = useTranslation();
  // §17.B13: related-children expand/contract (keyed by doc_report_band_id). This grid
  // receives only per-band element counts, not the element rows themselves, so it cannot
  // name children here — the control renders inert. TODO: wire relations if the band's
  // element rows are passed down.
  const detail = useExpandedRows<number>();
  const buildRelatedGroups = (_id: number): RelatedChildGroup[] => [];
  const hasRelated = (_id: number) => false;
  return (
    <div className="mb-4">
      <div className="mb-1 text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('rpd.bands', 'Bands')}</div>
      <PersonalizedGrid componentKey="bands-grid" columns={BAND_COLS}>
        {(visibleCols, widths) => (
          <table className={GRID_TABLE_CLASS}>
            <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
              <tr>
                <th className="w-7 border-b border-csm-border px-1 py-1" title={t('common.related_lbl', 'Related records')} />
                {visibleCols.map(col => (
                  <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left" style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                ))}
                <th className="border-b border-csm-border px-2 py-1 text-left" style={{ width: '1%' }}>{t('common.actions', 'Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {bands.map(b => (
                <Fragment key={b.doc_report_band_id}>
                <tr className="hover:bg-csm-surface-alt">
                  <td className="border-b border-csm-border-soft px-1 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                    <RelatedExpandButton
                      open={detail.isOpen(b.doc_report_band_id)}
                      hasChildren={hasRelated(b.doc_report_band_id)}
                      onToggle={() => detail.toggle(b.doc_report_band_id)}
                      expandLabel={t('common.related_expand', 'Show related records')}
                      collapseLabel={t('common.related_collapse', 'Hide related')}
                      tooltip={t('common.related_lbl', 'Related records')}
                    />
                  </td>
                  {visibleCols.map(col => (
                    <td key={col.key} className="border-b border-csm-border-soft px-2 py-1 text-csm-sm" style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                      {col.key === 'band'      && bandDisplay(b)}
                      {col.key === 'height_pt' && b.height_pt}
                      {col.key === 'elements'  && countFor(b.doc_report_band_id)}
                    </td>
                  ))}
                  <td className="border-b border-csm-border-soft px-2 py-1 text-right">
                    <button onClick={() => onRemove(b)} className="rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-0.5 text-csm-xs text-csm-danger hover:opacity-80">{t('rpd.remove', 'remove')}</button>
                  </td>
                </tr>
                {detail.isOpen(b.doc_report_band_id) && (
                  <tr>
                    <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                      <RelatedChildrenPanel groups={buildRelatedGroups(b.doc_report_band_id)} session={session} />
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
              {bands.length === 0 && (
                <tr><td colSpan={visibleCols.length + 2} className="py-6 text-center"><EmptyState title={t('rpd.no_bands', 'No bands yet')} /></td></tr>
              )}
            </tbody>
          </table>
        )}
      </PersonalizedGrid>
    </div>
  );
}

/* ── Elements list grid ─────────────────────────────────────────────────── */
const ELEMENT_COLS: GridColumnDef[] = [
  { key: 'band', header: 'Band', defaultWidth: 200 },
  { key: 'element_seq', header: 'Seq', defaultWidth: 70 },
  { key: 'element_type', header: 'Type', defaultWidth: 130 },
  { key: 'field_ref', header: 'Field / Expression', defaultWidth: 200 },
  { key: 'format_mask_code', header: 'Format Mask', defaultWidth: 200 },
];

function ElementsGrid({ session, elements, bandDisplay, elementLabel, maskName, onEdit }: {
  session: AuthSession;
  elements: ElementRow[]; bandDisplay: (bandId: number) => string; elementLabel: (v: string) => string;
  maskName: (code: string) => string; onEdit: (el: ElementRow) => void;
}) {
  const { t } = useTranslation();
  // §17.B13: related-children expand/contract (keyed by doc_report_element_id). Each
  // element's parent band is available as a resolved label via bandDisplay — surface it.
  const detail = useExpandedRows<number>();
  const buildRelatedGroups = (id: number): RelatedChildGroup[] => {
    const el = elements.find(e => e.doc_report_element_id === id);
    if (!el) return [];
    const label = bandDisplay(el.doc_report_band_id);
    return [
      {
        key: 'band',
        table: 'doc_report_band', pkColumn: 'doc_report_band_id',
        title: t('rpd.bands', 'Bands'),
        items: label ? [{ id: el.doc_report_band_id, label }] : [],
      },
    ];
  };
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);
  return (
    <div>
      <div className="mb-1 text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{t('rpd.elements', 'Elements')}</div>
      <PersonalizedGrid componentKey="elements-grid" columns={ELEMENT_COLS}>
        {(visibleCols, widths) => (
          <table className={GRID_TABLE_CLASS}>
            <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
              <tr>
                <th className="w-7 border-b border-csm-border px-1 py-1" title={t('common.related_lbl', 'Related records')} />
                {visibleCols.map(col => (
                  <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left" style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                ))}
                <th className="border-b border-csm-border px-2 py-1 text-left" style={{ width: '1%' }}>{t('common.actions', 'Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {elements.map(el => (
                <Fragment key={el.doc_report_element_id}>
                <tr className="hover:bg-csm-surface-alt">
                  <td className="border-b border-csm-border-soft px-1 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                    <RelatedExpandButton
                      open={detail.isOpen(el.doc_report_element_id)}
                      hasChildren={hasRelated(el.doc_report_element_id)}
                      onToggle={() => detail.toggle(el.doc_report_element_id)}
                      expandLabel={t('common.related_expand', 'Show related records')}
                      collapseLabel={t('common.related_collapse', 'Hide related')}
                      tooltip={t('common.related_lbl', 'Related records')}
                    />
                  </td>
                  {visibleCols.map(col => (
                    <td key={col.key} className="border-b border-csm-border-soft px-2 py-1 text-csm-sm" style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                      {col.key === 'band'             && bandDisplay(el.doc_report_band_id)}
                      {col.key === 'element_seq'      && el.element_seq}
                      {col.key === 'element_type'     && elementLabel(el.element_type)}
                      {col.key === 'field_ref'        && <span className="font-mono text-csm-xs">{el.field_ref || el.expression || <span className="text-csm-2xs text-csm-muted">—</span>}</span>}
                      {col.key === 'format_mask_code' && (el.format_mask_code ? maskName(el.format_mask_code) : <span className="text-csm-2xs text-csm-muted">—</span>)}
                    </td>
                  ))}
                  <td className="border-b border-csm-border-soft px-2 py-1 text-right">
                    <button onClick={e => { e.stopPropagation(); onEdit(el); }} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('common.edit', 'Edit')}</button>
                  </td>
                </tr>
                {detail.isOpen(el.doc_report_element_id) && (
                  <tr>
                    <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                      <RelatedChildrenPanel groups={buildRelatedGroups(el.doc_report_element_id)} session={session} />
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
              {elements.length === 0 && (
                <tr><td colSpan={visibleCols.length + 2} className="py-6 text-center"><EmptyState title={t('rpd.no_elements', 'No elements yet')} /></td></tr>
              )}
            </tbody>
          </table>
        )}
      </PersonalizedGrid>
    </div>
  );
}

/* ── Property Inspector modal ───────────────────────────────────────────── */
function PropertyInspector({ element, masks, maskName, onSave, onRemove, onClose }: {
  element: ElementRow; masks: FormatMaskOption[]; maskName: (code: string) => string;
  onSave: (patch: Partial<ElementRow>, el: ElementRow) => Promise<string | null>;
  onRemove: () => void; onClose: () => void;
}) {
  const { t } = useTranslation();
  const [elementType, setElementType] = useState(element.element_type);
  const [x, setX] = useState(String(element.x_pt));
  const [y, setY] = useState(String(element.y_pt));
  const [w, setW] = useState(String(element.width_pt));
  const [h, setH] = useState(String(element.height_pt));
  const [fieldRef, setFieldRef] = useState(element.field_ref ?? '');
  const [expression, setExpression] = useState(element.expression ?? '');
  const [maskCode, setMaskCode] = useState(element.format_mask_code ?? '');
  const [fontSize, setFontSize] = useState(String(element.style?.font_size ?? ''));
  const [bold, setBold] = useState(!!element.style?.bold);
  const [align, setAlign] = useState<'left' | 'center' | 'right'>(element.style?.align ?? 'left');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      const style: ElementStyle = { ...element.style };
      if (fontSize.trim()) style.font_size = Number(fontSize); else delete style.font_size;
      style.bold = bold;
      style.align = align;
      const err = await onSave({
        element_type: elementType,
        x_pt: Number(x), y_pt: Number(y), width_pt: Number(w), height_pt: Number(h),
        field_ref: fieldRef.trim() || null, expression: expression.trim() || null,
        format_mask_code: maskCode || null, style,
      }, element);
      if (err) throw new Error(err);
      onClose();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const num = 'h-8 w-full rounded border border-csm-border px-2 text-csm-sm';
  const lbl = 'mb-1 block text-csm-sm font-medium text-csm-ink-soft';

  return (
    <Modal
      title={t('rpd.inspector.title', 'Element Properties')}
      onClose={onClose}
      width="lg"
      headerAction={<PersonalizationToggle />}
      footer={
        <>
          <button onClick={onRemove} className="mr-auto rounded border border-csm-danger-border bg-csm-danger-soft px-3 py-1 text-csm-sm text-csm-danger hover:opacity-80">{t('rpd.inspector.delete', 'Delete Element')}</button>
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel', 'Cancel')}</button>
          <button onClick={() => void save()} disabled={busy} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>
        </>
      }
    >
      <PersonalizedField componentKey="inspector" fieldKey="element_type" label={t('rpd.inspector.type', 'Element Type')}>
        <label className={lbl}>{t('rpd.inspector.type', 'Element Type')}</label>
        <select value={elementType} onChange={e => setElementType(e.target.value)} className="mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-sm">
          {ELEMENT_TYPES.map(o => <option key={o.value} value={o.value}>{t(o.labelKey, o.fallback)}</option>)}
        </select>
      </PersonalizedField>

      <div className="mb-3 grid grid-cols-4 gap-2">
        <PersonalizedField componentKey="inspector" fieldKey="x_pt" label={t('rpd.inspector.x', 'X (pt)')}>
          <label className={lbl}>{t('rpd.inspector.x', 'X (pt)')}</label>
          <input type="number" value={x} onChange={e => setX(e.target.value)} className={num} />
        </PersonalizedField>
        <PersonalizedField componentKey="inspector" fieldKey="y_pt" label={t('rpd.inspector.y', 'Y (pt)')}>
          <label className={lbl}>{t('rpd.inspector.y', 'Y (pt)')}</label>
          <input type="number" value={y} onChange={e => setY(e.target.value)} className={num} />
        </PersonalizedField>
        <PersonalizedField componentKey="inspector" fieldKey="width_pt" label={t('rpd.inspector.w', 'Width (pt)')}>
          <label className={lbl}>{t('rpd.inspector.w', 'Width (pt)')}</label>
          <input type="number" value={w} onChange={e => setW(e.target.value)} className={num} />
        </PersonalizedField>
        <PersonalizedField componentKey="inspector" fieldKey="height_pt" label={t('rpd.inspector.h', 'Height (pt)')}>
          <label className={lbl}>{t('rpd.inspector.h', 'Height (pt)')}</label>
          <input type="number" value={h} onChange={e => setH(e.target.value)} className={num} />
        </PersonalizedField>
      </div>

      <PersonalizedField componentKey="inspector" fieldKey="field_ref" label={t('rpd.inspector.field_ref', 'Field Reference')}>
        <label className={lbl}>{t('rpd.inspector.field_ref', 'Field Reference')}</label>
        <input value={fieldRef} onChange={e => setFieldRef(e.target.value)} placeholder="row.customer_name" className="mb-3 h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm" />
      </PersonalizedField>

      <PersonalizedField componentKey="inspector" fieldKey="expression" label={t('rpd.inspector.expression', 'Expression')}>
        <label className={lbl}>{t('rpd.inspector.expression', 'Expression')}</label>
        <input value={expression} onChange={e => setExpression(e.target.value)} placeholder={t('rpd.inspector.expression_hint', 'row.qty * row.price')} className="mb-3 h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm" />
      </PersonalizedField>

      <PersonalizedField componentKey="inspector" fieldKey="format_mask_code" label={t('rpd.inspector.format_mask', 'Format Mask')}>
        <label className={lbl}>{t('rpd.inspector.format_mask', 'Format Mask')}</label>
        <select value={maskCode} onChange={e => setMaskCode(e.target.value)} className="mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-sm">
          <option value="">{t('rpd.inspector.no_mask', '(none)')}</option>
          {masks.map(m => <option key={m.format_mask_code} value={m.format_mask_code}>{maskName(m.format_mask_code)}</option> /* fk-display-ok: label is resolved "CODE — Name" via maskName; value must carry the stored code for the select to round-trip. */)}
        </select>
      </PersonalizedField>

      <div className="mb-3 grid grid-cols-3 gap-2">
        <PersonalizedField componentKey="inspector" fieldKey="font_size" label={t('rpd.inspector.font_size', 'Font Size')}>
          <label className={lbl}>{t('rpd.inspector.font_size', 'Font Size')}</label>
          <input type="number" value={fontSize} onChange={e => setFontSize(e.target.value)} className={num} />
        </PersonalizedField>
        <PersonalizedField componentKey="inspector" fieldKey="align" label={t('rpd.inspector.align', 'Alignment')}>
          <label className={lbl}>{t('rpd.inspector.align', 'Alignment')}</label>
          <select value={align} onChange={e => setAlign(e.target.value as 'left' | 'center' | 'right')} className={num}>
            {ALIGNMENTS.map(o => <option key={o.value} value={o.value}>{t(o.labelKey, o.fallback)}</option>)}
          </select>
        </PersonalizedField>
        <PersonalizedField componentKey="inspector" fieldKey="bold" label={t('rpd.inspector.bold', 'Bold')}>
          <label className={lbl}>{t('rpd.inspector.bold', 'Bold')}</label>
          <label className="flex h-8 items-center gap-2 text-csm-sm text-csm-ink-soft">
            <input type="checkbox" checked={bold} onChange={e => setBold(e.target.checked)} className="h-4 w-4 accent-gray-700" />
            {t('rpd.inspector.bold', 'Bold')}
          </label>
        </PersonalizedField>
      </div>

      {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
    </Modal>
  );
}

/* ── Add Band modal ─────────────────────────────────────────────────────── */
function AddBandModal({ existing, onAdd, onClose }: {
  existing: string[]; onAdd: (bandType: string, heightPt: number) => void; onClose: () => void;
}) {
  const { t } = useTranslation();
  // Non-group band types are singletons per template — hide any already present.
  const available = NON_GROUP_BAND_TYPES.filter(b => !existing.includes(b.value));
  const [bandType, setBandType] = useState(available[0]?.value ?? '');
  const [height, setHeight] = useState('40');

  return (
    <Modal
      title={t('rpd.add_band.title', 'Add Band')}
      onClose={onClose}
      width="md"
      footer={
        <>
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel', 'Cancel')}</button>
          <button onClick={() => bandType && onAdd(bandType, Number(height) || 0)} disabled={!bandType} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{t('rpd.add_band.action', 'Add')}</button>
        </>
      }
    >
      {available.length === 0 ? (
        <p className="text-csm-sm text-csm-ink-soft">{t('rpd.add_band.all_present', 'All standard bands already exist. Group bands are added with a group.')}</p>
      ) : (
        <>
          <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('rpd.add_band.type', 'Band Type')}</label>
          <select value={bandType} onChange={e => setBandType(e.target.value)} className="mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-sm">
            {available.map(o => <option key={o.value} value={o.value}>{t(o.labelKey, o.fallback)}</option>)}
          </select>
          <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('rpd.add_band.height', 'Height (pt)')}</label>
          <input type="number" value={height} onChange={e => setHeight(e.target.value)} className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm" />
        </>
      )}
    </Modal>
  );
}

/* ── Preview surface (sandboxed iframe; all text escaped) ────────────────── */
function PreviewSurface({ pages, pageSize }: { pages: PreviewPage[]; pageSize: PageSizeRow }) {
  const { t } = useTranslation();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const scale = 0.5; // fit the ~595pt-wide page into the 420px panel

  const html = useMemo(() => {
    const w = pageSize.width_pt * scale;
    const h = pageSize.height_pt * scale;
    const pageDivs = pages.map(page => {
      const prims = page.primitives.map(p => {
        const style = `position:absolute;left:${p.x * scale}px;top:${p.y * scale}px;width:${p.width * scale}px;height:${p.height * scale}px;`;
        if (p.kind === 'text') {
          const s = p.style ?? {};
          const isErr = p.text.startsWith('#ERR');
          const extra = `font-size:${(s.font_size ?? 9) * scale}px;` +
            `font-weight:${s.bold ? 700 : 400};` +
            `text-align:${s.align ?? 'left'};` +
            `color:${isErr ? '#dc2626' : (s.color ?? '#111827')};` +
            'overflow:hidden;line-height:1.1;';
          return `<div style="${style}${extra}">${esc(p.text)}</div>`;
        }
        if (p.kind === 'line') {
          return `<div style="${style}background:${p.color ?? '#111827'};"></div>`;
        }
        if (p.kind === 'rect') {
          const bg = p.fill ? `background:${p.fill};` : '';
          const bd = p.border ? `border:1px solid ${p.color ?? '#111827'};` : '';
          return `<div style="${style}${bg}${bd}"></div>`;
        }
        // image — never inject remote/data strings as markup; show a placeholder box.
        return `<div style="${style}border:1px dashed #9ca3af;"></div>`;
      }).join('');
      return `<div class="pg" style="position:relative;width:${w}px;height:${h}px;">${prims}</div>`;
    }).join('');
    return `<!doctype html><html><head><meta charset="utf-8"><style>` +
      `body{margin:0;background:#e5e7eb;font-family:system-ui,sans-serif;}` +
      `.pg{background:#fff;margin:0 auto 8px;box-shadow:0 1px 3px rgba(0,0,0,.2);}` +
      `</style></head><body>${pageDivs}</body></html>`;
  }, [pages, pageSize]);

  return (
    <>
      <p className="mb-2 text-csm-2xs text-csm-muted">{t('rpd.preview_pages', '{{count}} page(s) rendered', { count: pages.length })}</p>
      <iframe
        ref={iframeRef}
        title="report-preview"
        sandbox=""
        srcDoc={html}
        className="h-[560px] w-full rounded border border-csm-border bg-white"
      />
    </>
  );
}

export function ReportDesignerPage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={DESIGNER_PAGE} session={session}>
      <DesignerInner session={session} />
    </PersonalizationProvider>
  );
}
